import React, { useRef, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  scrollElementHorizontally,
  DIRECTION,
  HORIZONTAL_SCROLL,
} from '@utils/scroll';
import useIsDesktopLayout from '@hooks/useIsDesktopLayout';
import isFunction from '@commons/utils/object/isFunction';
import ButtonIcon from '@pedidosya/web-fenix/system/ButtonIcon';
import ChevronRight from '@pedidosya/web-fenix/icons/ChevronRight';
import ChevronLeft from '@pedidosya/web-fenix/icons/ChevronLeft';
import SwiperContainer from './components/Swiper';
import Last from './components/Last';
import Container from './components/Container';
import ButtonContainer from './components/ButtonContainer';
import useScrollEnd from './hooks/useScrollEnd';

const doScroll = (swiperId, scroll, direction) => {
  scrollElementHorizontally(`swiper__element_${swiperId}`, scroll, direction);
};

function Swiper({ children, className, id, onScrollFinish }) {
  const isDesktop = useIsDesktopLayout();
  const [scroll, setScroll] = useState(HORIZONTAL_SCROLL);
  const [showButtons, setShowButtons] = useState(isDesktop && !!id);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const parentRef = useRef(null);
  const childrenRef = useRef(null);
  const applyScroll = useMemo(() => isFunction(onScrollFinish), [
    onScrollFinish,
  ]);

  const updateCanScrollLeft = () => {
    const container = childrenRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      const maxScroll = container.scrollWidth - container.clientWidth;
      setCanScrollRight(maxScroll - container.scrollLeft > 1);
    }
  };

  useEffect(() => {
    if (parentRef?.current?.clientWidth > 0)
      setScroll(parentRef?.current?.clientWidth);
  }, [parentRef, childrenRef]);

  useEffect(() => {
    if (
      childrenRef?.current.lastChild.offsetLeft <=
      parentRef?.current?.clientWidth
    ) {
      setShowButtons(false);
    }
  }, [childrenRef, parentRef]);

  useEffect(() => {
    const container = childrenRef.current;
    if (!container) {
      return undefined;
    }
    updateCanScrollLeft();
    container.addEventListener('scroll', updateCanScrollLeft);
    return () => {
      container.removeEventListener('scroll', updateCanScrollLeft);
    };
  }, [childrenRef, children]);

  useScrollEnd(
    { refContainer: childrenRef, enabled: applyScroll },
    onScrollFinish,
  );

  return (
    <Container ref={parentRef}>
      {showButtons && canScrollRight && (
        <ButtonContainer>
          <ButtonIcon
            icon={<ChevronRight />}
            state="enabled"
            size="small"
            buttonStyle="oncontent"
            onClick={() => doScroll(id, scroll, DIRECTION.RIGHT)}
          />
        </ButtonContainer>
      )}
      <SwiperContainer
        ref={childrenRef}
        className={className}
        id={`swiper__element_${id}`}
      >
        {children}
        <Last />
      </SwiperContainer>
      {showButtons && canScrollLeft && (
        <ButtonContainer left>
          <ButtonIcon
            icon={<ChevronLeft />}
            state="enabled"
            size="small"
            buttonStyle="oncontent"
            onClick={() => doScroll(id, scroll, DIRECTION.LEFT)}
          />
        </ButtonContainer>
      )}
    </Container>
  );
}

Swiper.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  id: PropTypes.string.isRequired,
  onScrollFinish: PropTypes.func,
};

Swiper.defaultProps = {
  className: '',
};

export default styled(Swiper)``;
